import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import cogoToast from '@successtar/cogo-toast'
import { Button } from '@renderer/components'
import { GlassPanel } from '@renderer/components/BackgroundContainer'
import PinModal from '@renderer/components/PinModal'
import OnScreenKeyboard from '@renderer/components/OnScreenKeyboard'
import MidtransUtils, { VoucherOnlyRegionError } from '@renderer/utils/midtransUtils'
import { useInterval } from '@renderer/utils/useInterval'
import { useConfigStore } from '@renderer/stores/configState'
import { useSessionStateStore } from '@renderer/stores/sessionState'
import { useSettingsStore } from '@renderer/stores/settingsStore'
import { formatAmount, normalizeCurrency } from '@renderer/utils/formatCurrency'
import type { Package } from '@renderer/types/configState'

export interface AiEffectPaidInfo {
  amount: number
  label: string
  via: 'gateway' | 'voucher' | 'bypass'
}

interface PostSessionAiPaymentModalProps {
  isVisible: boolean
  packages: Package[]
  themePrimary: string
  onPaid: (info: AiEffectPaidInfo) => void
  onSkip: () => void
}

/**
 * Customer-facing payment for the AI effect (real money). Mirrors `UserReprintModal`'s payment
 * flow — package selection → Midtrans QR/Snap (or voucher-only rail) → status polling — but is
 * generic (no print) and themed with the project's primary color. On settlement (or operator PIN
 * bypass / cash voucher) it calls `onPaid`; the parent then runs the AI generation.
 */
const PostSessionAiPaymentModal: React.FC<PostSessionAiPaymentModalProps> = ({
  isVisible,
  packages,
  themePrimary,
  onPaid,
  onSkip,
}) => {
  const { t } = useTranslation()
  const { config: globalConfig } = useConfigStore()
  const { id, userPreferences, logActivity } = useSessionStateStore()
  const { password } = useSettingsStore()
  const projectCurrency = normalizeCurrency(globalConfig?.resolvedCurrency)

  const tier = globalConfig?.paymentSetupTier
  const hasCustomGateway = Boolean(globalConfig?.hasCustomGateway)
  const isVoucherOnlyRegion =
    tier === 'global_hidden' || (tier === 'localized_in_progress' && !hasCustomGateway)

  const activePackages = useMemo(
    () => packages.filter((pkg) => pkg.isActive !== false),
    [packages]
  )
  const [selectedId, setSelectedId] = useState<string>(() => activePackages[0]?.id ?? '')
  const selectedPackage = useMemo(
    () => activePackages.find((pkg) => (pkg.id ?? '') === selectedId) ?? activePackages[0],
    [activePackages, selectedId]
  )
  const currentPrice = selectedPackage?.price || 0
  const paymentTimeoutConfig = globalConfig?.pages?.payment?.settings?.paymentTimeout || 120

  const [isProcessing, setIsProcessing] = useState(false)
  const [showPayment, setShowPayment] = useState(false)
  const [showPinModal, setShowPinModal] = useState(false)
  const [showVoucherInput, setShowVoucherInput] = useState(false)
  const [voucherError, setVoucherError] = useState('')
  const [isVoucherLoading, setIsVoucherLoading] = useState(false)
  const [qrUrl, setQrUrl] = useState('')
  const [snapToken, setSnapToken] = useState('')
  const [chargeInfo, setChargeInfo] = useState<{ amount: number; currency: string } | null>(null)
  const [isCheckingPayment, setIsCheckingPayment] = useState(false)
  const [isExpired, setIsExpired] = useState(false)
  const [orderId, setOrderId] = useState<string | null>(null)

  const paymentHandledRef = useRef(false)

  useEffect(() => {
    if (!isVisible) {
      setShowPayment(false)
      setIsProcessing(false)
      setQrUrl('')
      setSnapToken('')
      setOrderId(null)
      setIsCheckingPayment(false)
      paymentHandledRef.current = false
    }
  }, [isVisible])

  const finishPaid = (via: AiEffectPaidInfo['via']): void => {
    if (paymentHandledRef.current) return
    paymentHandledRef.current = true
    setIsCheckingPayment(false)
    onPaid({ amount: currentPrice, label: selectedPackage?.description || 'AI effect', via })
  }

  const handlePay = async (): Promise<void> => {
    if (!selectedPackage) {
      cogoToast.error(t('result.postSessionAi.errorGeneric', 'Something went wrong.'))
      return
    }
    if (isVoucherOnlyRegion) {
      logActivity('AI effect redirected to voucher entry (voucher-only region)', {
        paymentSetupTier: tier,
      })
      setVoucherError('')
      setShowVoucherInput(true)
      return
    }
    setIsProcessing(true)
    setShowPayment(true)
    setIsExpired(false)
    setQrUrl('')
    setSnapToken('')
    paymentHandledRef.current = false
    try {
      const data = await MidtransUtils.initiatePayment(
        selectedPackage.price,
        id || '',
        paymentTimeoutConfig,
        globalConfig?._id || '',
        userPreferences.filterId || '',
        userPreferences.frameId || '',
        0,
        false,
        'midtrans',
        true // isAiEffect
      )
      if (!data.order_id) throw new Error('Failed to generate payment order')
      setOrderId(data.order_id)
      setChargeInfo({
        amount: Number(data.amount) || 0,
        currency: (data.charge_currency || data.currency || projectCurrency).toUpperCase(),
      })
      if (data.qr_url) {
        setQrUrl(data.qr_url.replace(/([^:]\/)\/.+/g, '$1'))
        setIsCheckingPayment(true)
        logActivity('AI effect payment initiated (QR)', {
          orderId: data.order_id,
          amount: selectedPackage.price,
        })
      } else if (data.snap_token) {
        setSnapToken(data.snap_token)
        setIsCheckingPayment(true)
        logActivity('AI effect payment initiated (Snap)', { orderId: data.order_id })
      } else {
        throw new Error('Invalid payment response')
      }
    } catch (error) {
      if (error instanceof VoucherOnlyRegionError) {
        setShowPayment(false)
        setIsProcessing(false)
        setVoucherError('')
        setShowVoucherInput(true)
        return
      }
      console.error('AI effect payment initiation error:', error)
      cogoToast.error(t('result.postSessionAi.errorGeneric', 'Something went wrong.'))
      setShowPayment(false)
      setIsProcessing(false)
    }
  }

  const openSnapPayment = (): void => {
    if (!snapToken || !window.snap) {
      cogoToast.error('Payment gateway not ready')
      return
    }
    window.snap.pay(snapToken, {
      onSuccess: () => finishPaid('gateway'),
      onPending: () => setIsCheckingPayment(true),
      onError: () => cogoToast.error('Payment failed'),
      onClose: () => {},
    })
  }

  const handleCancelPayment = (): void => {
    setShowPayment(false)
    setIsCheckingPayment(false)
    setOrderId(null)
    setSnapToken('')
    setQrUrl('')
    setIsProcessing(false)
  }

  const handlePinSubmit = (pin: string): void => {
    if (pin === password) {
      logActivity('AI effect payment bypassed with PIN')
      setShowPinModal(false)
      finishPaid('bypass')
    } else {
      cogoToast.error(t('payment.wrongCode', 'Incorrect code. Please try again.'))
    }
  }

  const handleVoucherSubmit = async (code: string): Promise<void> => {
    if (!selectedPackage) return
    const trimmed = (code || '').trim()
    if (!trimmed) {
      setVoucherError(t('checkout.invalidPromo', 'Invalid promo code'))
      return
    }
    setVoucherError('')
    setIsVoucherLoading(true)
    try {
      const subtotal = Math.max(0, selectedPackage.price ?? 0)
      const response = await fetch(`${process.env.VITE_API_URL}/api/promos/apply`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          code: trimmed,
          projectId: globalConfig?._id || '',
          amount: Math.round(subtotal),
        }),
      })
      const data = await response.json().catch(() => ({}))
      const isValid = response.ok && data.isValid
      if (!isValid) {
        const msg = data.message || data.error || t('checkout.invalidPromo', 'Invalid promo code')
        setVoucherError(msg)
        cogoToast.error(msg)
        return
      }
      if (data.isCash) {
        logActivity('AI effect cash voucher applied', { code: trimmed, amount: subtotal })
        cogoToast.success(t('checkout.promoApplied', 'Promo code applied successfully!'))
        setShowVoucherInput(false)
        finishPaid('voucher')
        return
      }
      const apiDiscount = Math.round(Number(data.discount) || 0)
      const finalAmount = Math.max(0, Math.round(subtotal - apiDiscount))
      cogoToast.info(
        t(
          'reprint.voucherDiscountedSettleOffline',
          'Voucher applied. Please settle {{amount}} with the operator to continue.',
          { amount: formatAmount(finalAmount, projectCurrency) }
        )
      )
    } catch (error) {
      console.error('AI effect voucher validation error:', error)
      const msg = t('checkout.promoValidationFailed', 'Failed to validate promo code. Please try again.')
      setVoucherError(msg)
      cogoToast.error(msg)
    } finally {
      setIsVoucherLoading(false)
    }
  }

  useInterval(
    async () => {
      if (paymentHandledRef.current || !orderId) return
      try {
        const status = await MidtransUtils.checkPaymentStatus(orderId)
        if (status.isExpired) {
          setIsExpired(true)
          setIsCheckingPayment(false)
          cogoToast.warn('Payment expired')
        } else if (status.paid) {
          cogoToast.success('Payment successful!')
          logActivity('AI effect payment successful (verified)', { orderId })
          finishPaid('gateway')
        }
      } catch (error) {
        console.error('Error checking AI effect payment status:', error)
      }
    },
    isCheckingPayment ? 3000 : null
  )

  if (!isVisible) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-6">
      <GlassPanel className="flex w-full max-w-lg flex-col items-center gap-6 p-8">
        {showPayment ? (
          <>
            <h2 className="text-2xl font-bold text-carbon">
              {t('reprint.scanToPay', 'Complete Payment')}
            </h2>
            <div className="flex h-[280px] w-[280px] items-center justify-center rounded-2xl bg-white p-6 shadow">
              {isExpired ? (
                <div className="flex flex-col items-center gap-2 text-red-600">
                  <p className="font-bold">Payment expired</p>
                  <button onClick={() => void handlePay()} className="text-sm underline">
                    Try again
                  </button>
                </div>
              ) : qrUrl ? (
                <img
                  src={qrUrl}
                  alt="Payment QR"
                  className="h-full w-full object-contain"
                  crossOrigin="anonymous"
                  onError={() => cogoToast.error('Failed to load QR code')}
                />
              ) : snapToken ? (
                <Button text="Pay Now" onClick={openSnapPayment} className="px-8 py-3" />
              ) : (
                <div
                  className="h-10 w-10 animate-spin rounded-full border-2 border-t-transparent"
                  style={{ borderColor: themePrimary, borderTopColor: 'transparent' }}
                />
              )}
            </div>
            <p className="text-2xl font-bold text-carbon">
              {formatAmount(currentPrice, projectCurrency)}
            </p>
            {chargeInfo && chargeInfo.currency !== projectCurrency ? (
              <p className="-mt-4 text-sm text-carbon/60">
                {formatAmount(chargeInfo.amount, chargeInfo.currency)}
              </p>
            ) : null}
            <p className="max-w-sm text-center text-sm text-carbon/70">
              {qrUrl
                ? t('reprint.paymentInstruction', 'Scan the QR code to complete purchase')
                : 'Tap Pay Now to complete your purchase securely.'}
            </p>
            <Button
              text={t('reprint.cancel', 'Cancel')}
              variant="secondary"
              onClick={handleCancelPayment}
              className="w-full max-w-xs"
            />
          </>
        ) : (
          <>
            <div className="text-center">
              <h1 className="text-2xl font-bold text-carbon">
                {t('result.postSessionAi.title', 'AI enhancement')}
              </h1>
              <p className="mt-1 text-sm text-carbon/70">
                {t('result.postSessionAi.subtitle', 'Optional polish for your photos.')}
              </p>
            </div>

            <div className="flex w-full flex-col gap-2">
              {activePackages.map((pkg, index) => {
                const pkgId = pkg.id ?? ''
                const selected = pkgId === (selectedPackage?.id ?? '')
                return (
                  <button
                    key={pkg.id ?? index}
                    type="button"
                    onClick={() => setSelectedId(pkgId)}
                    className={`flex items-center justify-between rounded-xl border-2 px-4 py-3 text-left transition ${
                      selected ? 'shadow-md' : 'border-transparent bg-white/60 hover:bg-white'
                    }`}
                    style={selected ? { borderColor: themePrimary } : { borderColor: 'rgba(0,0,0,0.08)' }}
                  >
                    <span className="font-medium text-carbon">
                      {pkg.description || t('result.postSessionAi.title', 'AI enhancement')}
                    </span>
                    <span className="font-bold text-carbon">
                      {formatAmount(pkg.price || 0, projectCurrency)}
                    </span>
                  </button>
                )
              })}
            </div>

            <div className="flex w-full flex-col gap-2">
              <Button
                text={
                  isVoucherOnlyRegion
                    ? t('reprint.useVoucher', 'Use Voucher Code')
                    : t('reprint.print', 'Pay')
                }
                onClick={() => void handlePay()}
                disabled={isProcessing || !selectedPackage}
                className="w-full"
              />
              <Button
                text={t('result.postSessionAi.skip', 'Skip')}
                variant="secondary"
                onClick={onSkip}
                className="w-full"
              />
            </div>
          </>
        )}
      </GlassPanel>

      <PinModal
        isOpen={showPinModal}
        onClose={() => setShowPinModal(false)}
        onSubmit={handlePinSubmit}
        title={t('payment.codeEntry.title', 'Enter Access Code')}
        subtitle={t('payment.codeEntry.subtitle', 'Enter 6-digit code to bypass payment')}
      />

      {showVoucherInput ? (
        <OnScreenKeyboard
          onEnter={handleVoucherSubmit}
          placeholder={t('checkout.voucherCodePlaceholder', 'Type or paste your code')}
          error={voucherError}
          isLoading={isVoucherLoading}
          onInputChange={() => setVoucherError('')}
          onClose={() => {
            setShowVoucherInput(false)
            setVoucherError('')
          }}
        />
      ) : null}
    </div>
  )
}

export default PostSessionAiPaymentModal
